import { useRef, useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import EndMarker from 'automation-ui-canvas/components/common/EndMarker';
import FlowValidation from 'automation-ui-flow-core/validation/FlowValidation';
import { getZoomLevel, isMinimapMinimized } from 'automation-ui-canvas/selectors/localStorageSelectors';
import * as LocalStorageKeys from 'automation-ui-canvas/constants/LocalStorageKeys';
import { mergeDeepWithLocalStorage } from 'automation-ui-canvas/actions/LocalStorageActions';
import { isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { getPortalId, getHubSpotUserId } from 'automation-ui-canvas/selectors/authSelectors';
import MinimapViewpane from 'automation-ui-canvas/components/canvas/MinimapViewpane';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { rgba } from 'foundations-components/transitional/utils/color';
import { View } from 'foundations-components/components/View';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import { Button } from 'foundations-components/transitional/components/Button';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import UIIcon from 'UIComponents/icon/UIIcon';
import { RemoveIcon } from 'foundations-assets';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import BranchName from 'automation-ui-canvas/components/canvas/BranchName';
import ErrorBoundary from 'automation-ui-canvas/components/errors/ErrorBoundary';
import ErrorMessage from 'automation-ui-canvas/components/errors/ErrorMessage';
import { LEFT_CANVAS_PADDING, MINIMAP_CONTAINER_WIDTH } from 'automation-ui-flow-core/canvas/graphConstants';
import * as MinimapInteractionTypes from 'automation-ui-canvas/constants/MinimapInteractionTypes';
import getNodeWrapperWidth from 'automation-ui-directed-graph/utils/getNodeWrapperWidth';
import DirectedGraph from 'automation-ui-directed-graph/DirectedGraph';
import { getActionIconAndColor } from 'automation-ui-action-features/utils/actionThemeUtils';
import ObjectTypeIcons, { DEFAULT_ICON } from 'automation-ui-action-features/constants/ObjectTypeIcons';
import ActionThemes from 'automation-ui-flow-core/canvas/ActionThemes';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { getNodeHeights } from 'automation-ui-canvas/utils/getNodeHeights';
import BranchLabel from 'automation-ui-canvas/components/canvas/BranchLabel';
import BranchDescription from 'automation-ui-canvas/components/canvas/BranchDescription';
import { useMemoize } from 'automation-ui-canvas/hooks/useMemoize';
import { useThrottledFunction } from 'automation-ui-canvas/hooks/useThrottledFunction';
import { useCanvasTopOffset } from 'automation-ui-canvas/utils/styleUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONTAINER_MARGIN = 10;
const MIN_SCALE = 0.1;
const CARD_BORDER_WIDTH = 10;
const CARD_PADDING = 20;
const CLOSED_PANEL_Z_INDEX = 1;
const OPEN_PANEL_Z_INDEX = 1111;
const EMPTY_DRAG_PREVIEW = new Image();
// blank 1x1 gif
EMPTY_DRAG_PREVIEW.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEAAAAALAAAAAABAAEAAAIBAAA=';
const StyledMinimapToggleContainer = styled.div.withConfig({
  displayName: "MinimapFlowCanvas__StyledMinimapToggleContainer",
  componentId: "sc-158r7gk-0"
})(["position:fixed;top:auto;", ""], ({
  isMinimized,
  theme
}) => {
  if (isMinimized) {
    return `
        left: ${CONTAINER_MARGIN}px;
        z-index: ${CLOSED_PANEL_Z_INDEX};
        margin-top: 40px;
        margin-left: ${theme.space['200']};
      `;
  } else {
    return `
        left: ${MINIMAP_CONTAINER_WIDTH}px;
        z-index: ${OPEN_PANEL_Z_INDEX + 1};
        transform: translateX(-50%);
      `;
  }
});
const StyledMinimapContainer = styled.div.withConfig({
  displayName: "MinimapFlowCanvas__StyledMinimapContainer",
  componentId: "sc-158r7gk-1"
})(["text-align:center;position:fixed;top:", "px;width:", "px;bottom:0;left:0;box-sizing:content-box;border-right:", " solid ", ";z-index:", ";background-color:", ";& > *{width:100%;height:100%;}"], ({
  canvasTopOffset
}) => canvasTopOffset, MINIMAP_CONTAINER_WIDTH, ({
  theme
}) => theme.borderWidth['100'], ({
  theme
}) => theme.color['border-core-hover'], OPEN_PANEL_Z_INDEX, ({
  theme
}) => rgba(theme.color['fill-tertiary-subtle'], 0.6));
const StyledScaledContainer = styled.div.withConfig({
  displayName: "MinimapFlowCanvas__StyledScaledContainer",
  componentId: "sc-158r7gk-2"
})(["transform-origin:0 0;width:100%;"]);
const StyledMinimapActionCard = styled.div.withConfig({
  displayName: "MinimapFlowCanvas__StyledMinimapActionCard",
  componentId: "sc-158r7gk-3"
})(["display:flex;justify-content:center;align-items:center;border-radius:", "px;", ""], CARD_BORDER_WIDTH, ({
  isFlowEnabled,
  hasErrors,
  theme
}) => {
  if (hasErrors) {
    return `border: ${CARD_BORDER_WIDTH}px solid ${theme.color[isFlowEnabled ? 'border-alert-default' : 'border-caution-default']};`;
  } else {
    return `border: ${CARD_BORDER_WIDTH}px solid ${theme.color['border-core-default']};`;
  }
});
function MinimapFlowCanvas({
  flow,
  canvasScrollElementRef,
  handleGetActionOutputsForTargetActionById,
  getHeaderTitle,
  flowValidation = new FlowValidation(),
  nodeWidth,
  nodeHeight,
  extensionDefinitions
}) {
  var _canvasScrollElementR;
  const minimapRef = useRef(null);
  const timeoutId = useRef(NaN);
  const [canvasHeight, setCanvasHeight] = useState(0);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [deferRender, setDeferRender] = useState(true);
  useEffect(() => {
    timeoutId.current = setTimeout(() => {
      setDeferRender(false);
    }, 1500);
    return () => {
      clearTimeout(timeoutId.current);
    };
  }, []);
  const flowId = flow ? flow.getFlowId() : null;
  const dispatch = useDispatch();
  const zoomLevel = useSelector(getZoomLevel(flowId));
  const isMinimized = useSelector(isMinimapMinimized);
  const portalId = useSelector(getPortalId);
  const userId = useSelector(getHubSpotUserId);
  const {
    offsetHeight,
    offsetWidth
  } = (_canvasScrollElementR = canvasScrollElementRef.current) !== null && _canvasScrollElementR !== void 0 ? _canvasScrollElementR : {
    offsetHeight: canvasHeight,
    offsetWidth: canvasWidth
  };
  useEffect(() => {
    setCanvasHeight(offsetHeight);
    setCanvasWidth(offsetWidth);
  }, [offsetHeight, offsetWidth]);
  const uiTheme = useTheme();
  const canvasTopOffset = useCanvasTopOffset(canvasScrollElementRef);
  const handleMinimize = useCallback(() => {
    dispatch(mergeDeepWithLocalStorage({
      [`${LocalStorageKeys.MINIMAP_MINIMIZED}-${portalId}-${userId}`]: !isMinimized
    }));
    trackEvent(Trackables.CLICK_TOGGLE_MINIMAP, {
      interactionType: isMinimized ? MinimapInteractionTypes.PANEL_OPEN : MinimapInteractionTypes.PANEL_CLOSE
    });
  }, [isMinimized, dispatch, portalId, userId]);
  const directedGraph = useMemo(() => {
    const showEnrollmentCard = !!flow && !flow.isActionSet();
    return translateToGraph(flow, false, nodeWidth, nodeHeight, showEnrollmentCard);
  }, [flow, nodeHeight, nodeWidth]);
  const firstNodeWrapperWidth = useMemo(() => getNodeWrapperWidth(directedGraph.getStartingNodeId(), directedGraph), [directedGraph]);
  const scale = useMemo(() => {
    return Math.max(MINIMAP_CONTAINER_WIDTH / (firstNodeWrapperWidth + 2 * LEFT_CANVAS_PADDING + 40), MIN_SCALE);
  }, [firstNodeWrapperWidth]);
  const scrollToMousePosition = useThrottledFunction((pageX, pageY, behavior = 'smooth') => {
    const minimapScrollLeft = minimapRef.current.scrollLeft;
    const minimapScrollTop = minimapRef.current.scrollTop;
    const scrollLeft = (pageX + minimapScrollLeft) / scale;
    const centerWidthOffset = canvasWidth / zoomLevel / 2;
    const canvasScrollLeft = (scrollLeft - centerWidthOffset) * zoomLevel;
    const scrollTop = (pageY - canvasTopOffset + minimapScrollTop) / scale;
    const centerHeightOffset = canvasHeight / zoomLevel / 2;
    const canvasScrollTop = (scrollTop - centerHeightOffset) * zoomLevel;
    canvasScrollElementRef.current.scrollTo({
      left: canvasScrollLeft,
      top: canvasScrollTop,
      behavior
    });
  }, 100, {
    leading: true,
    trailing: false
  });
  const handleMinimapClick = useCallback(event => scrollToMousePosition(event.pageX, event.pageY), [scrollToMousePosition]);
  const handleMinimapDrag = useCallback(event => {
    event.dataTransfer.setDragImage(EMPTY_DRAG_PREVIEW, 0, 0);
    scrollToMousePosition(event.pageX, event.pageY, 'instant');
  }, [scrollToMousePosition]);
  const handleMinimapDragEnd = useCallback(event => scrollToMousePosition(event.pageX, event.pageY), [scrollToMousePosition]);
  const getCardIcon = useCallback(id => {
    const action = flow.getActionById(id);
    if (isGoToActionId(id)) {
      return {
        iconName: 'next',
        iconUrl: undefined,
        theme: 'koala'
      };
    } else if (id === CanvasIdentifiers.FIRST_NODE) {
      const iconName = ObjectTypeIcons[flow.objectTypeId] || DEFAULT_ICON;
      return {
        iconName,
        iconUrl: undefined,
        theme: 'koala'
      };
    } else if (id === CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE) {
      return {
        iconName: 'date',
        iconUrl: undefined,
        theme: 'koala'
      };
    } else if (!action) {
      return {
        iconName: 'integrations',
        iconUrl: undefined,
        theme: 'koala'
      };
    } else if (action.actionType === ActionTypes.EMPTY_ACTION) {
      return {
        iconName: 'question',
        iconUrl: undefined,
        theme: 'koala'
      };
    }
    return getActionIconAndColor({
      action,
      extensionDefinitions
    });
  }, [extensionDefinitions, flow]);
  const onRenderNode = useMemoize(({
    id,
    width,
    height
  }) => {
    const actionId = id;
    const {
      iconName,
      iconUrl,
      theme
    } = getCardIcon(id);
    const {
      iconColor,
      headerBackgroundColor
    } = ActionThemes[theme];
    const invalidActions = flowValidation.getErrorsByActionId(actionId);
    const hasErrors = !!invalidActions.size;
    const size = Math.round(nodeHeight - (CARD_BORDER_WIDTH + CARD_PADDING) * 2);
    let icon;
    if (iconUrl) {
      icon = /*#__PURE__*/_jsx("img", {
        src: iconUrl,
        alt: "",
        height: size,
        width: size
      });
    } else {
      icon = /*#__PURE__*/_jsx(UIIcon, {
        name: iconName || 'integrations',
        size: size,
        color: iconColor || undefined
      });
    }
    if (actionId === CanvasIdentifiers.FIRST_NODE && flow && flow.isActionSet()) {
      return null;
    }
    return /*#__PURE__*/_jsx(UITooltip, {
      title: getHeaderTitle({
        actionId
      }),
      children: /*#__PURE__*/_jsx(StyledMinimapActionCard, {
        hasErrors: hasErrors,
        isFlowEnabled: !!(flow && flow.getIsEnabled()),
        style: {
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: headerBackgroundColor || undefined
        },
        children: icon
      })
    });
  }, ({
    id
  }) => `${id}-${uniqueIdForObject(flow)}-${flowValidation && uniqueIdForObject(flowValidation)}`);
  const renderEndMarker = edgeId => {
    if (flow && flow.isActionSet()) {
      return null;
    }
    return /*#__PURE__*/_jsx(EndMarker, {
      flow: flow,
      edgeId: edgeId,
      hideFooter: true
    });
  };
  const renderBranchName = edgeId => () => {
    return /*#__PURE__*/_jsx(View, {
      paddingInline: "400",
      paddingBlock: "200",
      children: /*#__PURE__*/_jsx(BranchName, {
        flow: flow,
        edgeId: edgeId,
        handleGetActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
      })
    });
  };
  const renderBranchLabel = edgeId => {
    return /*#__PURE__*/_jsx(UITooltip, {
      Content: renderBranchName(edgeId),
      children: /*#__PURE__*/_jsx(BranchLabel, {
        branchLabelHeaderText: /*#__PURE__*/_jsx(BranchName, {
          flow: flow,
          edgeId: edgeId,
          handleGetActionOutputsForTargetActionById: handleGetActionOutputsForTargetActionById
        }),
        branchDescription: /*#__PURE__*/_jsx(BranchDescription, {
          flow: flow,
          edgeId: edgeId
        }),
        hideFooter: true
      })
    });
  };
  const renderViewpane = () => {
    return /*#__PURE__*/_jsx(MinimapViewpane, {
      canvasHeight: canvasHeight,
      canvasWidth: canvasWidth,
      zoomLevel: zoomLevel,
      canvasScrollElementRef: canvasScrollElementRef,
      minimapScrollElementRef: minimapRef
    });
  };
  const renderGraph = () => {
    const showEnrollmentCard = !!flow && !flow.isActionSet();
    return /*#__PURE__*/_jsx(DirectedGraph, {
      onRenderNode: onRenderNode,
      onRenderBranchName: renderBranchLabel,
      onRenderConnectorButton: () => null,
      onRenderEndMarker: renderEndMarker,
      scrollElementRef: minimapRef,
      setFirstNodeRef: () => {},
      onNodeHeightsChanged: () => {},
      directedGraph: directedGraph,
      isMulti: false,
      isEmbedded: false,
      graphId: flow.getFlowId(),
      showOuterPadding: false,
      readOnly: true,
      disableVirtualRendering: false,
      zoomLevel: scale,
      showEdgeTooltips: false,
      nodeWidth: nodeWidth,
      nodeHeight: nodeHeight,
      nodeHeights: getNodeHeights({
        showEnrollmentCard
      }),
      goToLineStrokeWidth: Math.round(1 / scale)
    });
  };
  return /*#__PURE__*/_jsxs(ErrorBoundary, {
    boundaryName: "MinimapFlowCanvas",
    renderError: () => !isMinimized &&
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(StyledMinimapContainer, {
      canvasTopOffset: canvasTopOffset,
      children: /*#__PURE__*/_jsx(ErrorMessage, {})
    }),
    children: [/*#__PURE__*/_jsx(StyledMinimapToggleContainer, {
      isMinimized: isMinimized,
      children: isMinimized ? /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: handleMinimize,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.common.minimap.openMinimap"
        })
      }) : /*#__PURE__*/_jsx(IconButton, {
        use: "tertiary",
        shape: "circle",
        size: "sm",
        onClick: handleMinimize,
        children: /*#__PURE__*/_jsx(RemoveIcon, {})
      })
    }), /*#__PURE__*/_jsx(AccordionTransition, {
      open: !isMinimized,
      renderContent: () =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(StyledMinimapContainer, {
        canvasTopOffset: canvasTopOffset,
        draggable: true,
        onDragEnd: handleMinimapDragEnd,
        onDrag: handleMinimapDrag,
        onDragStart: handleMinimapDrag,
        onClick: handleMinimapClick,
        children: /*#__PURE__*/_jsx(UIScrollContainer, {
          scrollElementRef: minimapRef,
          gradientColor: uiTheme.color['border-core-default'],
          size: 7,
          children: deferRender ? /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {}) :
          /*#__PURE__*/
          // @bend-expect trellis-tokenized
          _jsxs(StyledScaledContainer, {
            style: {
              transform: `scale(${scale})`
            },
            children: [renderViewpane(), /*#__PURE__*/_jsx(View, {
              padding: "500",
              inlineSize: `${MINIMAP_CONTAINER_WIDTH / scale}px`,
              children: renderGraph()
            })]
          })
        })
      })
    })]
  });
}
export default MinimapFlowCanvas;